import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import LoadingBuffer from '../components/LoadingBuffer';
import { handleUniversalAddToCart, getProductStock } from '../utils/cartHelper';
import ProductCartButton from '../components/ProductCartButton';

// --- STYLES ---
const styles = `
  :root {
    --primary-purple: #633878;
    --accent-gold: #fbd349;
    --purple-hover: #74418c;
    --light-bg: #fdfbfd;
    --text-dark: #2c2c2c;
    --white: #ffffff;
    --border: #f0e6f5;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; font-family: 'Playfair Display', serif; }
  body { background-color: var(--light-bg); color: var(--text-dark); }

  /* Hero Banner */
  .banner {
    background: var(--primary-purple);
    color: var(--accent-gold);
    text-align: center;
    padding: 30px 20px;
    background-image: radial-gradient(circle, #74418c 1px, transparent 1px);
    background-size: 20px 20px;
  }
  .banner h1 { font-size: 1.8rem; color: var(--accent-gold); margin-bottom: 4px; font-weight: 700; letter-spacing: 1px; }

  /* Desktop Top Filter Bar */
  .top-filter-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 5%;
    background: white;
    border-bottom: 1px solid var(--border);
    position: sticky;
    top: 0;
    z-index: 100;
  }

  .sort-dropdown {
    padding: 6px 10px;
    border: 1px solid var(--border);
    border-radius: 4px;
    outline: none;
    color: var(--primary-purple);
    font-weight: 600;
    font-size: 0.85rem;
    background: #fdfafd;
    cursor: pointer;
  }

  /* Mobile Action Bar */
  .mobile-action-bar {
    display: none;
    background: white;
    border-bottom: 1px solid var(--border);
    border-top: 1px solid var(--border);
    position: sticky;
    top: 0;
    z-index: 100;
  }
  .mobile-action-grid {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    align-items: center;
  }
  .mobile-action-btn {
    background: white;
    border: none;
    padding: 12px 10px;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: var(--text-dark);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }
  .mobile-action-btn:nth-child(1) {
    border-right: 1px solid var(--border);
  }
  .mobile-grid-toggle-section {
    display: flex;
    border-left: 1px solid var(--border);
    padding: 0 8px;
    gap: 4px;
    align-items: center;
    height: 100%;
  }
  .grid-box-icon {
    width: 14px;
    height: 14px;
    border: 1.5px solid #888;
    background: #f5f5f5;
    cursor: pointer;
  }
  .grid-box-icon.active {
    background: var(--primary-purple);
    border-color: var(--primary-purple);
  }

  /* Main Layout */
  .shop-container {
    display: flex;
    padding: 30px 5%;
    gap: 30px;
  }

  /* Sidebar Filters */
  .sidebar { 
    width: 260px; 
    flex-shrink: 0; 
    background: white; 
    padding: 20px; 
    border-radius: 6px; 
    border: 1px solid var(--border); 
    height: fit-content; 
  }

  .mobile-sidebar-header {
    display: none;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 12px;
    margin-bottom: 12px;
    border-bottom: 1px solid var(--border);
  }
  .mobile-sidebar-header h3 {
    font-size: 0.9rem;
    color: var(--primary-purple);
    letter-spacing: 1.2px;
    text-transform: uppercase;
  }
  .btn-close-sidebar {
    background: none;
    border: none;
    font-size: 1.1rem;
    color: var(--text-dark);
    cursor: pointer;
    padding: 4px 8px;
  }
  
  .filter-section { 
    margin-bottom: 16px; 
    border-bottom: 1px solid var(--border);
    padding-bottom: 12px;
  }
  .filter-section:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
  
  .filter-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    font-size: 0.8rem; 
    text-transform: uppercase;
    letter-spacing: 1.2px; 
    color: var(--primary-purple); 
    font-weight: 700;
  }

  .arrow-icon { transition: transform 0.3s ease; font-size: 0.7rem; }
  .arrow-icon.open { transform: rotate(180deg); }

  .filter-content {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 180px;
    overflow-y: auto;
  }

  .filter-checkbox-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.8rem;
    color: #555;
    cursor: pointer;
  }
  .filter-checkbox-item input { accent-color: var(--primary-purple); width: 14px; height: 14px; }
  .color-dot { width: 10px; height: 10px; border-radius: 50%; border: 1px solid #ccc; display: inline-block; }

  /* Premium Modern Product Grid & Cards */
  .product-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 22px;
    width: 100%;
  }
  .product-grid.single-column {
    grid-template-columns: 1fr !important;
  }

  .product-card {
    background: white;
    position: relative;
    border-radius: 14px;
    overflow: hidden;
    border: 1px solid var(--border);
    box-shadow: 0 8px 24px rgba(99, 56, 120, 0.06);
    transition: transform 0.3s cubic-bezier(0.165, 0.84, 0.44, 1), box-shadow 0.3s ease, border-color 0.3s ease;
  }
  
  .product-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 36px rgba(99, 56, 120, 0.14);
    border-color: #d1b8dc;
  }

  .image-container { 
    position: relative; 
    height: 270px; 
    overflow: hidden; 
    background: #fbf9fc; 
  }
  .product-img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s cubic-bezier(0.165, 0.84, 0.44, 1); }
  
  /* Compact Hover Actions */
  .hover-actions {
    position: absolute;
    bottom: 0; left: 0; right: 0;
    display: flex;
    padding: 10px;
    gap: 8px;
    background: linear-gradient(to top, rgba(61, 20, 69, 0.92) 0%, rgba(61, 20, 69, 0.45) 75%, transparent 100%);
    backdrop-filter: blur(4px);
    transform: translateY(101%);
    transition: transform 0.25s ease-in-out;
  }
  .product-card:hover .hover-actions { transform: translateY(0); }
  .product-card:hover .product-img { transform: scale(1.05); }

  .btn-cart { 
    flex: 2; 
    background: linear-gradient(135deg, #fceea7 0%, #e2bc53 50%, #c99b27 100%); 
    border: none; 
    padding: 10px 14px; 
    cursor: pointer; 
    font-weight: 800; 
    color: #2a1236; 
    border-radius: 999px; 
    font-size: 0.76rem; 
    letter-spacing: 0.5px; 
    text-transform: uppercase;
    box-shadow: 0 4px 12px rgba(201, 155, 39, 0.35);
    transition: all 0.2s ease;
  }
  .btn-cart:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(201, 155, 39, 0.5); }
  
  .btn-view { 
    flex: 1; 
    background: white; 
    border: none; 
    padding: 10px 14px; 
    cursor: pointer; 
    border-radius: 999px; 
    font-size: 0.76rem; 
    color: var(--primary-purple); 
    font-weight: 700; 
    letter-spacing: 0.5px;
    text-transform: uppercase;
    box-shadow: 0 4px 10px rgba(0,0,0,0.12);
    transition: all 0.2s ease;
  }
  .btn-view:hover { transform: translateY(-2px); background: #fdfafd; box-shadow: 0 6px 14px rgba(0,0,0,0.18); }

  .badge {
    position: absolute; top: 10px; left: 10px; padding: 4px 10px;
    border-radius: 999px; font-size: 0.65rem; font-weight: 700; z-index: 2; letter-spacing: 0.8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }
  .badge.new { background: #3d1445; color: #fbeea4; border: 1px solid rgba(226, 188, 83, 0.4); }
  .badge.bestseller { background: linear-gradient(135deg, #e2bc53, #c99b27); color: #2a1236; }
  
  .discount {
    position: absolute; top: 38px; left: 10px; 
    background: linear-gradient(135deg, #b31e28 0%, #85121a 100%);
    color: white; padding: 3px 9px; border-radius: 999px; font-size: 0.65rem; z-index: 2; font-weight: 700;
    box-shadow: 0 2px 8px rgba(179, 30, 40, 0.3);
  }

  /* Compact Product Info */
  .product-info { padding: 14px; background: #ffffff; }
  .product-cat { color: #927598; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 1px; font-weight: 600; margin-bottom: 3px; }
  .product-name { font-weight: 600; margin: 2px 0 6px 0; font-size: 0.95rem; color: #3b1443; line-height: 1.3; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  
  .rating-row { display: flex; align-items: center; gap: 4px; margin-bottom: 6px; }
  .stars { color: #e6af2e; font-size: 0.75rem; }
  .review-count { color: #888; font-size: 0.7rem; }

  .price-row { display: flex; gap: 8px; align-items: center; }
  .current-price { color: #3d1445; font-weight: 700; font-size: 1.05rem; }
  .old-price { text-decoration: line-through; color: #a48da7; font-size: 0.85rem; }

  /* Sort Modal (Mobile) */
  .sort-modal-overlay {
    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
    background: rgba(0,0,0,0.5); z-index: 1002; display: flex; align-items: flex-end;
  }
  .sort-modal-content {
    background: white; width: 100%; padding: 20px; border-radius: 12px 12px 0 0;
    display: flex; flex-direction: column; gap: 12px; animation: slideUp 0.25s ease;
  }
  .sort-modal-header {
    display: flex; justify-content: space-between; align-items: center;
    border-bottom: 1px solid var(--border); padding-bottom: 6px;
  }
  .sort-modal-content h3 { font-size: 0.9rem; color: var(--primary-purple); text-transform: uppercase; letter-spacing: 1px; }
  .sort-option-item { padding: 10px 0; font-size: 0.85rem; cursor: pointer; border-bottom: 1px solid #f8f8f8; font-weight: 500; }
  .sort-option-item.selected { color: var(--primary-purple); font-weight: 700; }

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }

  .cart-success-overlay{
    position:fixed; top:0; left:0; width:100%; height:100%;
    background:rgba(99,56,120,0.3); backdrop-filter: blur(2px);
    display:flex; justify-content:center; align-items:center; z-index:99999;
  }
  .cart-success-box{
    width:320px; background:#ffffff; padding:25px; border-radius:16px; text-align:center;
    border: 1px solid var(--accent-gold); box-shadow: 0 12px 35px rgba(99,56,120,0.25);
  }
  .cart-success-icon{ font-size:35px; margin-bottom:10px; }
  .cart-success-box h3{ color:var(--primary-purple); font-size:18px; margin-bottom:6px; font-weight: 700; }
  .cart-success-box p{ color:#666; font-size:13px; }

  /* Mobile Grid adjustments */
  @media (max-width: 768px) {
    .top-filter-bar { display: none; }
    .mobile-action-bar { display: block; }
    .mobile-sidebar-header { display: flex; }
    .shop-container { flex-direction: column; padding: 15px 3%; gap: 15px; }
    .sidebar {
      position: fixed; left: -100%; top: 0; height: 100%; background: white;
      z-index: 1001; width: 280px; padding: 15px; box-shadow: 5px 0 25px rgba(0,0,0,0.15);
      overflow-y: auto; border-radius: 0; transition: left 0.3s ease;
    }
    .sidebar.open { left: 0; }
    .sidebar-overlay {
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.4); z-index: 1000; display: none;
    }
    .product-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
    .image-container { height: 205px; }
    .hover-actions {
      transform: translateY(0);
      background: linear-gradient(to top, rgba(61, 20, 69, 0.88) 0%, rgba(61, 20, 69, 0.35) 80%, transparent 100%);
      padding: 6px;
      gap: 6px;
    }
    .btn-cart, .btn-view {
      padding: 8px 6px;
      font-size: 0.74rem;
      border-radius: 999px;
      min-height: 33px;
      touch-action: manipulation;
      transition: transform 0.15s ease;
    }
    .btn-cart:active, .btn-view:active {
      transform: scale(0.96);
    }
  }
`;

export default function ShopPage() {
  const [selectedPriceRanges, setSelectedPriceRanges] = useState([]);
  const [selectedOccasions, setSelectedOccasions] = useState([]);
  const [selectedFabrics, setSelectedFabrics] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  
  const [openSections, setOpenSections] = useState({
    availability: true,
    price: false,
    category: false,
    fabric: false,
    color: false
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isSortModalOpen, setSortModalOpen] = useState(false);
  const [isSingleColumn, setIsSingleColumn] = useState(false);
  const [sortOption, setSortOption] = useState("Featured");
  const [inStockOnly, setInStockOnly] = useState(false);
  const [products, setProducts] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cartPopup, setCartPopup] = useState({ show: false, message: '' });

  const normalizeCat = (val = '') =>
    String(val || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');

  const showCartPopup = (message) => {
    setCartPopup({ show: true, message });
    setTimeout(() => setCartPopup({ show: false, message: "" }), 2000);
  };

  const navigate = useNavigate();

  const handleCheckboxChange = (list, setList, item) => {
    if (list.includes(item)) {
      setList(list.filter(i => i !== item));
    } else {
      setList([...list, item]);
    }
  };

  const priceRanges = [
    { label: "₹500.00 - ₹999.00", min: 500, max: 999 },
    { label: "₹1000.00 - ₹1999.00", min: 1000, max: 1999 },
    { label: "₹2000.00 - ₹2999.00", min: 2000, max: 2999 },
    { label: "₹3000.00 - ₹4999.00", min: 3000, max: 4999 },
    { label: "₹5000.00 - ₹6999.00", min: 5000, max: 6999 },
    { label: "₹7000.00+", min: 7000, max: 100000 }
  ];

  const displayedProducts = products
    .filter(p => {
      const matchesPrice = selectedPriceRanges.length === 0 || selectedPriceRanges.some(range => {
        const found = priceRanges.find(r => r.label === range);
        return found && p.price >= found.min && p.price <= found.max;
      });

      const matchesOccasion =
        selectedOccasions.length === 0 ||
        selectedOccasions.some(
          (o) =>
            normalizeCat(p.cat) === normalizeCat(o) ||
            p.cat.toLowerCase().includes(o.toLowerCase())
        );
      const matchesFabric = selectedFabrics.length === 0 || selectedFabrics.includes(p.fabric);
      const matchesColor = selectedColors.length === 0 || (p.color && selectedColors.includes(p.color));
      const matchesStock = !inStockOnly || getProductStock(p) > 0;

      return (selectedPriceRanges.length === 0 || matchesPrice) && matchesOccasion && matchesFabric && matchesColor && matchesStock;
    })
    .sort((a, b) => {
      if (sortOption === "Price: Low to High") return a.price - b.price;
      if (sortOption === "Price: High to Low") return b.price - a.price;
      return 0;
    });

  const fetchReviewsForProduct = async (productId) => {
    try {
      const res = await fetch(`https://api.vanyacollectionsrajahmundry.com/review/${productId}`);
      return await res.json();
    } catch (err) {
      return [];
    }
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [response, catRes] = await Promise.all([
          fetch('https://api.vanyacollectionsrajahmundry.com/products/all'),
          fetch('https://api.vanyacollectionsrajahmundry.com/categories').catch(() => null),
        ]);
        const productsData = await response.json();
        const catData = catRes && catRes.ok ? await catRes.json() : [];
        const validCats = Array.isArray(catData) ? catData : [];
        setApiCategories(validCats);

        const activeProducts = (Array.isArray(productsData) ? productsData : []).filter((item) => !item.is_hold);

        const productsWithReviews = await Promise.all(
          activeProducts.map(async (item) => {
            const reviews = await fetchReviewsForProduct(item.id);
            const totalReviews = reviews.length;
            const avgRating = totalReviews ? reviews.reduce((sum, r) => sum + r.rating, 0) / totalReviews : 0;
            const primaryImg =
              item.img_url ||
              item.image ||
              (Array.isArray(item.thumbnails) && item.thumbnails[0]) ||
              (Array.isArray(item.variants) && item.variants[0]?.mainImage) ||
              (Array.isArray(item.variants) && item.variants[0]?.thumbnails?.[0]) ||
              "";

            const normItemCat = normalizeCat(item.category);
            const matchedCat = validCats.find(
              (c) => normalizeCat(c?.category) === normItemCat
            );
            const resolvedCat = matchedCat?.category || item.category || "";

            return {
              ...item,
              id: item.id,
              name: item.name,
              category: resolvedCat,
              cat: resolvedCat,
              fabric: item.fabric || "Pure Silk",
              colour: item.colour || item.color || null,
              color: item.colour || item.color || null,
              price: Number(item.price || 0),
              oldPrice: Number(item.old_price || item.oldPrice || 0),
              stock: Number(item.stock ?? 0),
              rating: Math.round(avgRating * 10) / 10,
              reviews: totalReviews,
              badge: Number(item.discount) > 20 ? "BESTSELLER" : "NEW",
              discount: `-${item.discount || 0}%`,
              img: primaryImg,
              img_url: primaryImg,
              image: primaryImg,
              imageUrl: primaryImg,
              variants: Array.isArray(item.variants) ? item.variants : [],
              thumbnails: Array.isArray(item.thumbnails) ? item.thumbnails : (primaryImg ? [primaryImg] : []),
            };
          })
        );
        setProducts(productsWithReviews);
      } catch (error) {
        console.error("Failed to fetch products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();

    const handleSync = () => fetchProducts();
    window.addEventListener("focus", handleSync);
    window.addEventListener("storage", handleSync);
    window.addEventListener("vanya_category_sync", handleSync);
    return () => {
      window.removeEventListener("focus", handleSync);
      window.removeEventListener("storage", handleSync);
      window.removeEventListener("vanya_category_sync", handleSync);
    };
  }, []);

  const handleAddToCart = async (product) => {
    try {
      const availableStock = getProductStock(product);
      if (availableStock <= 0) {
        alert(`"${product.name}" is currently out of stock.`);
        return;
      }
      const result = await handleUniversalAddToCart(product);
      if (result.outOfStock) {
        alert(result.message || `"${product.name}" is currently out of stock.`);
        return;
      }
      if (result.needsColourSelection) {
        alert("Please select a colour first.");
        navigate(`/product/${product.id}`);
        return;
      }
      showCartPopup(result.message || `${product.name} added to your bag ✨`);
    } catch (error) {
      console.error("Add to cart error:", error);
      showCartPopup("Something went wrong");
    }
  };

  const occasionsList = React.useMemo(() => {
    const list = [];
    const seen = new Set();
    apiCategories.forEach((c) => {
      const name = String(c?.category || "").trim();
      const key = normalizeCat(name);
      if (name && key && !seen.has(key)) {
        seen.add(key);
        list.push(name);
      }
    });
    products.forEach((p) => {
      const name = String(p?.cat || p?.category || "").trim();
      const key = normalizeCat(name);
      if (name && key && !seen.has(key)) {
        seen.add(key);
        list.push(name);
      }
    });
    return list.length > 0
      ? list
      : [
          'DesignerSarees', 'Wedding Collections', 'COTTON Sarees', 'SILK SAREES',
          'PartyWear', 'Weaving Mistake Sarees', 'Dress Materials', 'Budget Friendly Sarees',
          'Work Sarees', 'Damage Sarees', 'Pattu Sarees', 'Readymade Blouses', 'Handloom Sarees', 'Exclusive Sarees'
        ];
  }, [apiCategories, products]);

  const fabricsList = ['Pure Banarasi Silk', 'Pure Kanjeevaram Silk', 'Silk Embroidery', 'Pure Silk'];
  const colorsList = ['Red', 'Blue', 'Gold', 'Green', 'Maroon', 'Pink'];

  return (
    <>
      <style>{styles}</style>
      
      <header className="banner">
        <p style={{fontSize: '0.75rem', letterSpacing: '2px', fontWeight: '600', color: 'var(--accent-gold)'}}>ROYAL HERITAGE WEAVES</p>
        <h1>All Sarees</h1>
        <p style={{color: '#f5e6b8', fontSize: '0.85rem'}}>{displayedProducts.length} Products</p>
      </header>

      {/* Desktop Filter Bar */}
      <div className="top-filter-bar">
        <div style={{fontSize: '0.85rem', color: '#666'}}>Showing {displayedProducts.length} products</div>
        <select className="sort-dropdown" value={sortOption} onChange={(e) => setSortOption(e.target.value)}>
          <option>Featured</option>
          <option>Price: Low to High</option>
          <option>Price: High to Low</option>
        </select>
      </div>

      {/* Mobile Action Bar */}
      <div className="mobile-action-bar">
        <div className="mobile-action-grid">
          <button className="mobile-action-btn" onClick={() => setSidebarOpen(true)}>
            <span>🎚️</span> FILTER
          </button>
          <button className="mobile-action-btn" onClick={() => setSortModalOpen(true)}>
            <span>⇅</span> SORT BY
          </button>
          <div className="mobile-grid-toggle-section">
            <div 
              className={`grid-box-icon ${!isSingleColumn ? 'active' : ''}`} 
              onClick={() => setIsSingleColumn(false)}
              title="2 Columns"
            ></div>
            <div 
              className={`grid-box-icon ${isSingleColumn ? 'active' : ''}`} 
              onClick={() => setIsSingleColumn(true)}
              title="1 Column"
            ></div>
          </div>
        </div>
      </div>

      {/* Mobile Sort Bottom Modal */}
      {isSortModalOpen && (
        <div className="sort-modal-overlay" onClick={() => setSortModalOpen(false)}>
          <div className="sort-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="sort-modal-header">
              <h3>Sort By</h3>
              <button className="btn-close-sidebar" onClick={() => setSortModalOpen(false)}>✕</button>
            </div>
            {["Featured", "Price: Low to High", "Price: High to Low"].map((option) => (
              <div 
                key={option}
                className={`sort-option-item ${sortOption === option ? 'selected' : ''}`}
                onClick={() => { setSortOption(option); setSortModalOpen(false); }}
              >
                {option}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={`sidebar-overlay ${isSidebarOpen ? 'visible' : ''}`} onClick={() => setSidebarOpen(false)}></div>

      <div className="shop-container">
        <aside className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
          
          <div className="mobile-sidebar-header">
            <h3>Filters</h3>
            <button className="btn-close-sidebar" onClick={() => setSidebarOpen(false)}>✕</button>
          </div>

          <div className="filter-section">
            <div className="filter-header" onClick={() => toggleSection('availability')}>
              <span>Availability</span>
              <span className={`arrow-icon ${openSections.availability ? 'open' : ''}`}>▼</span>
            </div>
            {openSections.availability && (
              <div className="filter-content">
                <label className="filter-checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={inStockOnly} 
                    onChange={(e) => setInStockOnly(e.target.checked)} 
                  /> In Stock Only
                </label>
              </div>
            )}
          </div>

          <div className="filter-section">
            <div className="filter-header" onClick={() => toggleSection('price')}>
              <span>Price</span>
              <span className={`arrow-icon ${openSections.price ? 'open' : ''}`}>▼</span>
            </div>
            {openSections.price && (
              <div className="filter-content">
                {priceRanges.map(range => (
                  <label key={range.label} className="filter-checkbox-item">
                    <input 
                      type="checkbox" 
                      checked={selectedPriceRanges.includes(range.label)}
                      onChange={() => handleCheckboxChange(selectedPriceRanges, setSelectedPriceRanges, range.label)}
                    />
                    {range.label}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="filter-section">
            <div className="filter-header" onClick={() => toggleSection('category')}>
              <span>Category</span>
              <span className={`arrow-icon ${openSections.category ? 'open' : ''}`}>▼</span>
            </div>
            {openSections.category && (
              <div className="filter-content">
                {occasionsList.map(o => (
                  <label key={o} className="filter-checkbox-item">
                    <input 
                      type="checkbox" 
                      checked={selectedOccasions.includes(o)}
                      onChange={() => handleCheckboxChange(selectedOccasions, setSelectedOccasions, o)}
                    />
                    {o}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="filter-section">
            <div className="filter-header" onClick={() => toggleSection('fabric')}>
              <span>Fabric</span>
              <span className={`arrow-icon ${openSections.fabric ? 'open' : ''}`}>▼</span>
            </div>
            {openSections.fabric && (
              <div className="filter-content">
                {fabricsList.map(f => (
                  <label key={f} className="filter-checkbox-item">
                    <input 
                      type="checkbox" 
                      checked={selectedFabrics.includes(f)}
                      onChange={() => handleCheckboxChange(selectedFabrics, setSelectedFabrics, f)}
                    />
                    {f}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="filter-section">
            <div className="filter-header" onClick={() => toggleSection('color')}>
              <span>Color</span>
              <span className={`arrow-icon ${openSections.color ? 'open' : ''}`}>▼</span>
            </div>
            {openSections.color && (
              <div className="filter-content">
                {colorsList.map(c => (
                  <label key={c} className="filter-checkbox-item">
                    <input 
                      type="checkbox" 
                      checked={selectedColors.includes(c)}
                      onChange={() => handleCheckboxChange(selectedColors, setSelectedColors, c)}
                    />
                    <span className="color-dot" style={{backgroundColor: c.toLowerCase()}}></span>
                    {c}
                  </label>
                ))}
              </div>
            )}
          </div>

        </aside>    

        <main className={`product-grid ${isSingleColumn ? 'single-column' : ''}`}>
          {loading ? (
            <div style={{ gridColumn: '1 / -1', width: '100%' }}>
              <LoadingBuffer 
                message="Discovering Handloom Masterpieces..." 
                minHeight="450px" 
              />
            </div>
          ) : displayedProducts.length === 0 ? (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '60px 20px', color: '#666', fontFamily: 'Montserrat, sans-serif' }}>
              <p style={{ fontSize: '18px', color: '#3d1445', fontWeight: 600 }}>No products match your filters.</p>
              <p style={{ fontSize: '13px', color: '#777' }}>Try clearing filters to explore all sarees.</p>
            </div>
          ) : (
            displayedProducts.map(product => {
              const isOutOfStock = getProductStock(product) <= 0;
              return (
                <div key={product.id} className="product-card">
                  <div className="image-container">
                    {isOutOfStock ? (
                      <span className="badge outofstock" style={{ background: '#b91c1c', color: '#fff', border: '1px solid #ef4444' }}>
                        OUT OF STOCK
                      </span>
                    ) : (
                      <span className={`badge ${product.badge.toLowerCase()}`}>{product.badge}</span>
                    )}
                    <span className="discount">{product.discount}</span>
                    <img src={product.img} alt={product.name} className="product-img" />
                    <div className="hover-actions">
                      <button className="btn-view" onClick={() => navigate(`/product/${product.id}`)}>View</button>
                      <ProductCartButton product={product} buttonText="+ Add" />
                    </div>
                  </div>
                <div className="product-info">
                  <p className="product-cat">{product.cat}</p>
                  <h2 className="product-name">{product.name}</h2>
                  <div className="rating-row">
                    <div className="stars">
                      {"★".repeat(Math.floor(product.rating))}
                      {product.rating % 1 >= 0.5 ? "½" : ""}
                    </div>
                    <span className="review-count">({product.reviews || 0})</span>
                  </div>
                  <div className="price-row">
                    <span className="current-price">₹{product.price.toLocaleString()}</span>
                    <span className="old-price">₹{product.oldPrice.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            );
            })
          )}
        </main>

        {cartPopup.show && (
          <div className="cart-success-overlay">
            <div className="cart-success-box">
              <div className="cart-success-icon">🛍️</div>
              <h3>Cart Updated</h3>
              <p>{cartPopup.message}</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}